import React from 'react';
import { useTranslation } from 'react-i18next';
import type { UserStatistics } from '../types';

interface UserStatsProps {
  statistics: UserStatistics | null;
  isLoading: boolean;
}

const UserStats: React.FC<UserStatsProps> = ({ statistics, isLoading }) => {
  const { t } = useTranslation();
  const cards = [
    {
      key: 'total',
      label: t('admin.users.stats.total'),
      value: statistics?.total,
      detail: t('admin.users.stats.standard_users', { count: statistics?.user ?? 0 }),
      icon: 'group',
      iconClass: 'bg-primary-fixed text-primary',
    },
    {
      key: 'staff',
      label: t('admin.users.stats.staff'),
      value: statistics?.staff,
      detail: t('admin.users.stats.staff_description'),
      icon: 'badge',
      iconClass: 'bg-secondary-container text-on-secondary-container',
    },
    {
      key: 'manager',
      label: t('admin.users.stats.manager'),
      value: statistics?.manager,
      detail: t('admin.users.stats.manager_description'),
      icon: 'supervisor_account',
      iconClass: 'bg-tertiary-fixed text-tertiary',
    },
    {
      key: 'admin',
      label: t('admin.users.stats.admin'),
      value: statistics?.admin,
      detail: t('admin.users.stats.admin_description'),
      icon: 'admin_panel_settings',
      iconClass: 'bg-success-container text-on-success-container',
    },
  ];

  return (
    <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 sm:gap-md" aria-label={t('admin.users.stats.label')}>
      {cards.map((card) => (
        <article
          key={card.key}
          className="flex min-h-[132px] items-center justify-between gap-4 rounded-[20px] border border-outline-variant/70 bg-white p-5 shadow-sm"
        >
          <div className="min-w-0">
            <p className="font-label-sm text-label-sm uppercase tracking-wide text-secondary">{card.label}</p>
            {isLoading ? (
              <div className="mt-3 h-8 w-16 animate-pulse rounded bg-surface-container" />
            ) : (
              <p className="mt-1 text-[28px] font-bold leading-9 text-on-surface">
                {card.value?.toLocaleString() ?? '—'}
              </p>
            )}
            <p className="mt-1 truncate text-body-sm text-secondary">{card.detail}</p>
          </div>
          <span className={`material-symbols-outlined flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${card.iconClass}`}>
            {card.icon}
          </span>
        </article>
      ))}
    </section>
  );
};

export default UserStats;
